import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Instagram as InstagramIcon,
  Schedule as ScheduleIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import InstagramService from '../../services/instagram.service';

const InstagramDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<any>({
    caption: '',
    mediaUrl: '',
    mediaType: 'image' as 'image' | 'video' | 'carousel' | 'reel',
    postType: 'feed' as 'feed' | 'story' | 'reel',
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await InstagramService.getAllPosts({
        populate: '*',
        'pagination[pageSize]': 50,
        'sort[0]': 'createdAt:desc',
      });
      setPosts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    try {
      await InstagramService.createPost(formData);
      setOpenDialog(false);
      setFormData({ caption: '', mediaUrl: '', mediaType: 'image', postType: 'feed' });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await InstagramService.deletePost(id);
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      draft: 'default',
      scheduled: 'info',
      published: 'success',
      failed: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <InstagramIcon fontSize="large" color="secondary" />
          <Typography variant="h4">Instagram</Typography>
        </Stack>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          New Post
        </Button>
      </Stack>

      {/* Analytics Summary */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="error">
            {posts.reduce((sum, p) => sum + (p.attributes?.likes || 0), 0)}
          </Typography>
          <Typography color="text.secondary">Total Likes</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">
            {posts.reduce((sum, p) => sum + (p.attributes?.comments || 0), 0)}
          </Typography>
          <Typography color="text.secondary">Total Comments</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main">
            {posts.reduce((sum, p) => sum + (p.attributes?.reach || 0), 0)}
          </Typography>
          <Typography color="text.secondary">Total Reach</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="secondary">
            {posts.length}
          </Typography>
          <Typography color="text.secondary">Total Posts</Typography>
        </Paper>
      </Stack>

      {/* Posts Grid */}
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {posts.map((post) => (
            <Card key={post.id}>
              {post.attributes?.mediaUrl && (
                <CardMedia
                  component="img"
                  height="200"
                  image={post.attributes.mediaUrl}
                  alt={post.attributes?.caption}
                />
              )}
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={post.attributes?.status}
                      size="small"
                      color={getStatusColor(post.attributes?.status)}
                    />
                    <Chip label={post.attributes?.postType} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" noWrap>
                    {post.attributes?.caption}
                  </Typography>
                  <Stack direction="row" spacing={2} mt={1}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <FavoriteIcon fontSize="small" color="error" />
                      <Typography variant="caption">
                        {post.attributes?.likes || 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CommentIcon fontSize="small" color="primary" />
                      <Typography variant="caption">
                        {post.attributes?.comments || 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <VisibilityIcon fontSize="small" color="action" />
                      <Typography variant="caption">
                        {post.attributes?.reach || 0}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate(`/instagram/${post.id}`)}>
                  Edit
                </Button>
                <IconButton size="small" color="error" onClick={() => handleDelete(post.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Create Post Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Instagram Post</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={2}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Caption"
              value={formData.caption}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              placeholder="Write your caption... #hashtags @mentions"
            />
            <TextField
              fullWidth
              label="Media URL"
              value={formData.mediaUrl}
              onChange={(e) => setFormData({ ...formData, mediaUrl: e.target.value })}
              placeholder="https://..."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                select
                label="Media Type"
                value={formData.mediaType}
                onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
              >
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="carousel">Carousel</MenuItem>
                <MenuItem value="reel">Reel</MenuItem>
              </TextField>
              <TextField
                fullWidth
                select
                label="Post Type"
                value={formData.postType}
                onChange={(e) => setFormData({ ...formData, postType: e.target.value })}
              >
                <MenuItem value="feed">Feed</MenuItem>
                <MenuItem value="story">Story</MenuItem>
                <MenuItem value="reel">Reel</MenuItem>
              </TextField>
            </Stack>
            <Alert severity="info">
              Post will be saved as draft. You can schedule or publish it later.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePost} color="secondary">
            Create Draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstagramDashboard;

